import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { IMessage } from '../types/types';

interface UseWebSocketParams {
  visitorId: string | null;
  conversationId: string | null;
  onMessage: (message: IMessage) => void;
  onStatusChange: (status: string) => void;
  onBotChunk?: (chunk: string) => void;
  onComplete?: (message?: string) => void;
  onError?: (error: string) => void;
}

export const useWebSocket = ({
  visitorId,
  conversationId,
  onMessage,
  onStatusChange,
  onBotChunk,
  onComplete,
  onError,
}: UseWebSocketParams) => {
  const socketRef = useRef<Socket | null>(null);

  // Initialize WebSocket connection
  useEffect(() => {
    if (!visitorId) {
      console.log('⏸️ No visitorId provided, skipping WebSocket connection');
      return;
    }

    const apiUrl = import.meta.env.VITE_API_URL;
    if (!apiUrl) {
      console.warn('⚠️ No API URL configured for WebSocket');
      return;
    }

    const socketURL = apiUrl.replace(/\/api\/?$/, '');

    console.log('🔌 Initializing WebSocket connection...');
    console.log('🌐 Socket URL:', socketURL);
    console.log('👤 Visitor ID:', visitorId);

    const socket = io(socketURL, {
      reconnectionDelay: 1000,
      reconnection: true,
      reconnectionAttempts: 10,
      transports: ['websocket', 'polling'],
      autoConnect: true,
      timeout: 20000,
      path: '/socket.io/',
      query: {
        visitorId,
        type: 'visitor',
      },
    });

    socketRef.current = socket;

    // Track if we've joined the conversation room to prevent duplicate joins
    const joinedConversationRef = { current: false };

    // Connection events
    socket.on('connect', () => {
      console.log('✅ WebSocket connected!');
      console.log('🆔 Socket ID:', socket.id);

      console.log('🚀 Joining visitor room:', visitorId);
      socket.emit('join:visitor', visitorId);

      // Join conversation room if conversationId exists
      const currentConversationId = conversationId;
      if (currentConversationId) {
        console.log('🚀 Joining conversation room on connect:', currentConversationId);
        socket.emit('join:conversation', currentConversationId);
        joinedConversationRef.current = true;
      } else {
        console.log('⏸️ No conversationId yet, will join when available');
      }
    });

    // Listen for room join acknowledgments
    socket.on('joined:visitor', (data) => {
      console.log('✅ Successfully joined visitor room:', data);
    });

    socket.on('joined:conversation', (data) => {
      console.log('✅ Successfully joined conversation room:', data);
    });

    socket.on('disconnect', (reason) => {
      console.log('🔌 WebSocket disconnected:', reason);
      if (reason === 'io server disconnect') {
        socket.connect();
      }
    });

    socket.on('connect_error', (error) => {
      console.error('❌ WebSocket connection error:', error.message);
    });

    socket.on('reconnect', (attemptNumber) => {
      console.log(`🔄 Reconnected after ${attemptNumber} attempts`);
      
      // Rejoin visitor room
      console.log('🔄 Rejoining visitor room:', visitorId);
      socket.emit('join:visitor', visitorId);
      
      // Rejoin conversation room if exists
      if (conversationId) {
        console.log('🔄 Rejoining conversation room:', conversationId);
        socket.emit('join:conversation', conversationId);
      }
    });

    // Listen for new messages (user messages and agent messages)
    socket.on('message:new', (message: any) => {
      console.log('📨 New message received via WebSocket:', message);

      const mapType = (msgType: any): IMessage['type'] => {
        const t = String(msgType || '').toLowerCase();
        if (t === 'workflow') return 'workflow';
        if (t === 'image') return 'image';
        if (t === 'video') return 'video';
        if (t === 'audio') return 'audio';
        if (t === 'document' || t === 'file') return 'document';
        // default to text for any other message types (buttons, lists, etc.)
        return 'text';
      };

      const formattedMessage: IMessage = {
        _id: message._id,
        content: message.content,
        isFromUser: !!message.isFromUser,
        createdAt: new Date(message.createdAt || Date.now()),
        type: mapType(message.messageType),
        mediaUrl: message.mediaUrl,
        workflowData: message.workflowData || null,
      };

      onMessage(formattedMessage);
    });

    // Listen for bot response chunks (streaming)
    socket.on('message:bot:chunk', ({ chunk }: { chunk: string }) => {
      console.log('📝 Bot chunk received:', chunk.substring(0, 50));
      if (onBotChunk) {
        onBotChunk(chunk);
      }
    });

    // Listen for bot response completion
    socket.on('message:complete', ({ message }: { message?: string }) => {
      console.log('✅ Bot response complete', message ? `with ${message.length} messages` : '');
      if (onComplete) {
        onComplete(message);
      }
    });

    // Listen for error messages
    socket.on('message:error', ({ error }: { error: string }) => {
      console.error('❌ Error from server:', error);
      if (onError) {
        onError(error);
      }
    });

    // Listen for conversation status changes
    socket.on('conversation:status', ({ conversationId: convId, status }) => {
      console.log(`🔔 Conversation ${convId} status changed to: ${status}`);
      onStatusChange(status);
    });

    // Listen for message processing status (e.g., "Checking workflow", "Executing workflow")
    socket.on('message:processing', ({ statusText }: { status: string; statusText: string }) => {
      console.log('⏳ Processing status:', statusText);
      // This could be used to show loading status text in the future
    });

    // Workflow message - complete workflow node
    // Backend sends workflow messages with this event
    socket.on('message:workflow', (message: any) => {
      console.log('📨 Workflow message received:', message);
      if (onMessage) {
        // Ensure workflow messages conform to IMessage
        const workflowMsg: IMessage = {
          _id: message._id,
          content: message.content || message.text || '',
          isFromUser: !!message.isFromUser,
          createdAt: new Date(message.createdAt || Date.now()),
          type: 'workflow',
          workflowData: message.workflowData || message,
        };
        onMessage(workflowMsg);
      }
    });

    // // Listen for workflow timeout events
    // socket.on('workflow:timeout', (workflowResult: any) => {
    //   console.log('⏰ Workflow timeout event received:', workflowResult);
    //   if (onMessage) {
    //     // Convert workflow result to message format
    //     // Use a composite ID to ensure uniqueness and enable duplicate detection
    //     const workflowMessage: IMessage = {
    //       _id: `workflow_${workflowResult.sessionId}_${Date.now()}`,
    //       content: workflowResult.text || workflowResult.body || '[Workflow Message]',
    //       isFromUser: false,
    //       createdAt: new Date(),
    //       type: 'workflow',
    //       workflowData: workflowResult,
    //     };
    //     onMessage(workflowMessage);
    //   }
    // });

    // // Listen for workflow auto-execute next node events
    // socket.on('workflow:next', (workflowResult: any) => {
    //   console.log('🔗 Workflow auto-execute next event received:', workflowResult);
    //   if (onMessage) {
    //     // Convert workflow result to message format
    //     // Use a composite ID to ensure uniqueness and enable duplicate detection
    //     const workflowMessage: IMessage = {
    //       _id: `workflow_${workflowResult.sessionId}_${Date.now()}`,
    //       content: workflowResult.text || workflowResult.body || '[Workflow Message]',
    //       isFromUser: false,
    //       createdAt: new Date(),
    //       type: 'workflow',
    //       workflowData: workflowResult,
    //     };
    //     onMessage(workflowMessage);
    //   }
    // });

    // Cleanup
    return () => {
      console.log('🧹 Cleaning up WebSocket connection');
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [visitorId]);

  // Update conversation room when conversationId changes
  useEffect(() => {
    const socket = socketRef.current;
    
    console.log('🔍 ConversationId effect triggered:', {
      hasSocket: !!socket,
      isConnected: socket?.connected,
      conversationId,
    });
    
    if (!conversationId) {
      console.log('⏸️ ConversationId not available yet');
      return;
    }

    if (!socket) {
      console.log('⏳ Socket not initialized yet');
      return;
    }

    // Use a small delay to ensure socket.connected state is updated
    const joinRoom = () => {
      if (socket.connected) {
        console.log('👤 Joining conversation room (conversationId changed):', conversationId);
        socket.emit('join:conversation', conversationId);
      } else {
        console.log('⏳ Socket not connected yet, will retry in 100ms');
        // Retry after a short delay
        setTimeout(joinRoom, 100);
      }
    };

    joinRoom();
  }, [conversationId]);

  return socketRef;
};
